// Alertas de completitud EN VIVO (2026-09-10, pedido explícito del usuario)
// — a diferencia de calidad-hc.ts (packages/web, retrospectivo/agregado por
// muestreo), esto corre en el navegador del propio médico, contra su sesión
// de Medplum, para UNA consulta puntual. Las 3 piezas NO son equivalentes:
//   - anamnesis-ingreso: de una sola vez en la vida del paciente, se mira el
//     HISTORIAL completo (mismo alcance que calidad-hc.ts para este ítem).
//   - notas-evolucion: por CADA consulta, se mira SOLO el Encounter de hoy.
//   - epicrisis-alta: solo aplica si el Encounter de hoy es de internación
//     (class IMP) y YA se dio de alta (status finished) — un IMP que sigue
//     abierto no debe alertar, el paciente sigue internado.
// Sin Encounter abierto hoy (el médico solo entró a mirar la ficha, no iba
// a ver a este paciente hoy) no se evalúa nada — ver evaluarCompletitudAlSalir.
import type { MedplumClient } from '@medplum/core';
import type { Encounter } from '@medplum/fhirtypes';
import { buscarEncounterAbiertoDeHoy } from './asegurarEncounterDeHoy';

const SISTEMA_FORMULARIOS_MSP = 'http://logiacare.ec/fhir/formularios-msp';
/** `Composition.type` de las anamnesis discretas 002 y 003 (búsqueda OR por coma). */
const TIPOS_COMPOSITION_ANAMNESIS = `${SISTEMA_FORMULARIOS_MSP}|002,${SISTEMA_FORMULARIOS_MSP}|003`;
const CODIGO_LOINC_EPICRISIS = '18842-5';
const V3_ACT_CODE = 'http://terminology.hl7.org/CodeSystem/v3-ActCode';

export type ClaveHallazgoCompletitud = 'anamnesis-ingreso' | 'notas-evolucion' | 'epicrisis-alta';

/** Código MSP de anamnesis que corresponde al Encounter de HOY: 002 (Consulta Externa) para todo lo que no sea internación, 003 (Hospitalización) para IMP. */
export type CodigoAnamnesis = '002' | '003';

export type HallazgoIncompleto = {
  clave: ClaveHallazgoCompletitud;
  /** `Encounter/<id>` de la consulta a la que aplica — para armar el link directo desde el widget del dashboard. */
  encounterRef: string;
  /**
   * Solo para `anamnesis-ingreso` — a qué formulario debe apuntar el link
   * ("continuar"), según la clase del Encounter de HOY. Bug real encontrado
   * en vivo 2026-09-11: el link estaba fijo en Formulario 003
   * (Hospitalización) sin importar la clase del Encounter, así que una
   * consulta ambulatoria (AMB, Formulario 002) mandaba al médico a un
   * formulario que su especialidad no tiene habilitada (003 vive en el
   * paquete `hospitalizacion` de catalogoEspecialidades.ts, 002 en `base`) —
   * "sin acceso" pese a que el formulario correcto (002) sí lo tenía.
   */
  codigoFormulario?: CodigoAnamnesis;
};

function codigoAnamnesisDe(encounter: Encounter): CodigoAnamnesis {
  return encounter.class?.system === V3_ACT_CODE && encounter.class.code === 'IMP' ? '003' : '002';
}

async function existeAlgunResultado(medplum: MedplumClient, resourceType: 'QuestionnaireResponse' | 'ClinicalImpression' | 'Composition', query: string): Promise<boolean> {
  const encontrados = await medplum.searchResources(resourceType, `${query}&_count=1`);
  return encontrados.length > 0;
}

/**
 * Alcance de vida entera del paciente: cuenta como "hecha" con CUALQUIERA de
 * los dos formularios de anamnesis del Anexo 1 (002 Consulta Externa o 003
 * Hospitalización) — antes solo miraba 003, así que un paciente 100%
 * ambulatorio con su 002 completo aparecía eternamente como "falta
 * anamnesis". Cuál de los dos se ofrece para COMPLETAR (el link) lo decide
 * `codigoAnamnesisDe` según la clase del Encounter de hoy, no esta función.
 */
async function faltaAnamnesis(medplum: MedplumClient, patientId: string): Promise<boolean> {
  const [q002, q003] = await Promise.all([
    medplum.searchOne('Questionnaire', { identifier: `${SISTEMA_FORMULARIOS_MSP}|002` }),
    medplum.searchOne('Questionnaire', { identifier: `${SISTEMA_FORMULARIOS_MSP}|003` }),
  ]);
  const idsAplicados = [...new Set([q002?.id, q003?.id].filter((id): id is string => !!id))];
  if (idsAplicados.length === 0) return false; // ni 002 ni 003 están aplicados a este tenant — nada que exigir.
  const referencias = idsAplicados.map((id) => `Questionnaire/${id}`).join(',');
  const tieneAlguna = await existeAlgunResultado(
    medplum,
    'QuestionnaireResponse',
    `subject=${encodeURIComponent(`Patient/${patientId}`)}&questionnaire=${encodeURIComponent(referencias)}&status=completed`,
  );
  if (tieneAlguna) return false;
  // 002/003 ya son formularios discretos (Fase 7): al guardar dejan una `Composition` con este `type`, que cuenta igual
  // que el `QuestionnaireResponse` histórico.
  const tieneComposition = await existeAlgunResultado(
    medplum,
    'Composition',
    `subject=${encodeURIComponent(`Patient/${patientId}`)}&type=${encodeURIComponent(TIPOS_COMPOSITION_ANAMNESIS)}&status=final`,
  );
  return !tieneComposition;
}

async function faltaEvolucion(medplum: MedplumClient, encounterRef: string): Promise<boolean> {
  const tiene = await existeAlgunResultado(medplum, 'ClinicalImpression', `encounter=${encodeURIComponent(encounterRef)}&status=completed`);
  return !tiene;
}

async function faltaEpicrisis(medplum: MedplumClient, encounterRef: string): Promise<boolean> {
  const tiene = await existeAlgunResultado(
    medplum,
    'Composition',
    `encounter=${encodeURIComponent(encounterRef)}&type=${encodeURIComponent(`http://loinc.org|${CODIGO_LOINC_EPICRISIS}`)}`,
  );
  return !tiene;
}

function esInternacionDadaDeAlta(encounter: Encounter): boolean {
  return encounter.class?.system === V3_ACT_CODE && encounter.class.code === 'IMP' && encounter.status === 'finished';
}

/**
 * Corre las 3 reglas para el Encounter de HOY de un paciente. Se llama tanto
 * al salir de la ficha (AlertaCompletitudPopup.tsx) como desde el widget del
 * dashboard del médico (AlertasCompletitudPropias.tsx, con el Encounter ya
 * resuelto de una búsqueda propia — por eso recibe el Encounter opcional en
 * vez de siempre resolverlo, para no repetir el search).
 */
export async function evaluarCompletitudDeEncounter(medplum: MedplumClient, patientId: string, encounter: Encounter): Promise<HallazgoIncompleto[]> {
  if (!encounter.id) return [];
  const encounterRef = `Encounter/${encounter.id}`;
  const hallazgos: HallazgoIncompleto[] = [];

  const [anamnesisFalta, evolucionFalta] = await Promise.all([faltaAnamnesis(medplum, patientId), faltaEvolucion(medplum, encounterRef)]);
  if (anamnesisFalta) hallazgos.push({ clave: 'anamnesis-ingreso', encounterRef, codigoFormulario: codigoAnamnesisDe(encounter) });
  if (evolucionFalta) hallazgos.push({ clave: 'notas-evolucion', encounterRef });

  if (esInternacionDadaDeAlta(encounter)) {
    const epicrisisFalta = await faltaEpicrisis(medplum, encounterRef);
    if (epicrisisFalta) hallazgos.push({ clave: 'epicrisis-alta', encounterRef });
  }

  return hallazgos;
}

/** Punto de entrada para "al salir de la ficha" — resuelve el Encounter de hoy (sin crearlo) y evalúa. `[]` si no hay ninguno (no se iba a atender a este paciente hoy). */
export async function evaluarCompletitudAlSalir(medplum: MedplumClient, patientId: string): Promise<HallazgoIncompleto[]> {
  const encounter = await buscarEncounterAbiertoDeHoy(medplum, patientId);
  if (!encounter) return [];
  return evaluarCompletitudDeEncounter(medplum, patientId, encounter);
}
